import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { PageHeaderComponent } from '../../../page-header/page-header.component';
import { OrderCommentsCardComponent } from '../../../order-details/order-comments-card/order-comments-card.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface ReflexOrderDetails {
  orderNo: string;
  statusNo: number;
  statusText: string;
  isBilled: number;
  isPaid: number;
  customerNo: number;
  addressNo: number;
  companyShortName: string;
  companyName: string;
  contact: string;
  dateFrom: string;
  dateTo: string;
}

interface CoinUsageInfo {
  found: boolean;
  coinsUsed?: number;
  coinValue?: number;
  totalCoinValue?: number;
  status?: string;
  usedBy?: string;
  usedByEmail?: string;
  createdDate?: string;
}

@Component({
  selector: 'app-reflex-order-details',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatButtonModule,
    PageHeaderComponent,
    OrderCommentsCardComponent
  ],
  templateUrl: './reflex-order-details.component.html',
  styleUrl: './reflex-order-details.component.css'
})
export class ReflexOrderDetailsComponent implements OnInit, OnDestroy {
  orderDetails$: Observable<ReflexOrderDetails | null> = of(null);
  orderId: number = 0;
  customerNo: number = 0;
  isLoading: boolean = true;
  error: string | null = null;
  coinUsageInfo: CoinUsageInfo | null = null;

  private baseUrl = `${environment.backend.baseUrl}${environment.backend.apiUrl}/`;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.orderId = Number(params.get('id')!);
      if (this.orderId) {
        // First, search for the order to get customer number
        this.searchOrderToGetCustomerNo();
      }
    });
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  searchOrderToGetCustomerNo(): void {
    this.isLoading = true;
    this.error = null;

    // First call: Search for order to get customer number
    this.http.get<any[]>(`${this.baseUrl}Gateway/orders/like/${this.orderId}`)
      .pipe(
        catchError(error => {
          console.error('Error searching order:', error);
          this.error = 'Fehler beim Suchen des Auftrags';
          this.isLoading = false;
          return of([]);
        })
      )
      .subscribe(orders => {
        if (orders && orders.length > 0) {
          // Find exact match (compare as strings since orderNo can be number or string from API)
          const exactOrder = orders.find(o => String(o.orderNo) === String(this.orderId));
          if (exactOrder && exactOrder.customerNo) {
            this.customerNo = exactOrder.customerNo;
            this.loadOrderDetails();
          } else {
            this.error = 'Auftrag nicht gefunden oder keine Kundennummer vorhanden';
            this.isLoading = false;
          }
        } else {
          this.error = 'Auftrag nicht gefunden';
          this.isLoading = false;
        }
      });
  }

  loadOrderDetails(): void {
    this.isLoading = true;
    this.error = null;

    // Load order details from Gateway
    const orderDetails$ = this.http.get<ReflexOrderDetails>(
      `${this.baseUrl}Gateway/customers/${this.customerNo}/orders/${this.orderId}`
    ).pipe(
      catchError(error => {
        console.error('Error loading order details:', error);
        this.error = 'Fehler beim Laden der Auftragsdetails';
        return of(null);
      })
    );

    // Load coin usage info from backend Order table
    const coinUsage$ = this.http.get<CoinUsageInfo>(
      `${this.baseUrl}Orders/coin-usage/${this.orderId}`
    ).pipe(
      catchError(error => {
        console.error('Error loading coin usage info:', error);
        return of({ found: false } as CoinUsageInfo);
      })
    );

    // Execute both requests in parallel
    forkJoin([orderDetails$, coinUsage$]).subscribe(([orderDetails, coinUsage]) => {
      this.orderDetails$ = of(orderDetails);
      this.coinUsageInfo = coinUsage;
      this.isLoading = false;
    });
  }

  back(): void {
    this.location.back();
  }
}
